import { Component, OnInit, ViewChild, inject } from "@angular/core";
import { ProcessEditorComponent } from "@app/process-editor/process-editor.component";
import { HistoryOverviewComponent } from "@app/shared/history-overview/history-overview.component";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";
import { Pages } from "@constants/pages/Pages";
import { nameof } from "@methods/jeffs-toolkit";
import { TabMenuItem } from "@models/generic/TabModels/TabMenuItem";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { ApplicationPermissionsModel } from "@models/permissions/ApplicationPermissionsModel";
import { PermissionsService } from "@services/permissions/PermissionsService";
import { TabService } from "@services/tabs/TabService";
import { ToastrService } from "ngx-toastr";
import { BusinessNavigatorPermissionModel } from "../models/business-navigator-permission-model";
import { BusinessNavigatorRequestModel } from "../models/business-navigator-request-model";
import { BusinessNavigatorDataService } from "../services/business-navigator-data.service";
import { CerrixPromptService } from "./../../../shared/services/cerrix-prompt.service";
import { BusinessDocumentsComponent } from "./components/business-documents/business-documents.component";
import { BusinessHyperlinksComponent } from "./components/business-hyperlinks/business-hyperlinks.component";
import { BusinessAssessmentDocumentsComponent } from "./components/business-assessment-documents/business-assessment-documents.component";
import { ApplicationSettings } from "@services/http/settings/application-settings";
import { SettingsDataService } from "@services/http/SettingsDataService";
import { firstValueFrom } from "rxjs";
import { CerrixLoaderComponent } from "@app/shared/components/cerrix-loader/cerrix-loader.component";
import { MenuSystemDirective } from "@app/shared/directives/menu-system.directive";
import { DetailOptionsComponent } from "@app/shared/components/detail-options/detail-options.component";
import { BusinessDetailsComponent } from "./components/business-details/business-details.component";
import { BusinessReviewComponent } from "./components/business-review/business-review.component";
import { BusinessRisksWidgetComponent } from "./components/business-risks/risks-widget/business-risks-widget.component";
import { BusinessRisksComponent } from "./components/business-risks/risks-overview/business-risks.component";
import { BusinessControlsComponent } from "./components/business-controls/business-controls.component";
import { BusinessIncidentsComponent } from "./components/business-incidents/business-incidents.component";
import { BusinessEventsComponent } from "./components/business-events/business-events.component";
import { BusinessMoisComponent } from "./components/business-mois/business-mois.component";
import { BusinessKrisComponent } from "./components/business-kris/business-kris.component";
import { BusinessDataprocessingComponent } from "./components/business-dataprocessing/business-dataprocessing.component";
import { BusinessThirdPartiesComponent } from "./components/business-thirdparties/business-thirdparties.component";

@Component({
    selector: "business-navigator-detail",
    templateUrl: "./business-navigator-detail.component.html",
    imports: [
        CerrixLoaderComponent,
        MenuSystemDirective,
        BusinessDetailsComponent,
        BusinessReviewComponent,
        BusinessRisksWidgetComponent,
        BusinessRisksComponent,
        BusinessControlsComponent,
        BusinessIncidentsComponent,
        BusinessEventsComponent,
        BusinessMoisComponent,
        BusinessKrisComponent,
        BusinessDataprocessingComponent,
        BusinessThirdPartiesComponent,
        ProcessEditorComponent,
        BusinessDocumentsComponent,
        BusinessAssessmentDocumentsComponent,
        BusinessHyperlinksComponent,
        HistoryOverviewComponent,
        DetailOptionsComponent,
    ],
})
export class BusinessNavigatorDetailComponent implements OnInit {
    dataService = inject(BusinessNavigatorDataService);
    private promptService = inject(CerrixPromptService);
    private toastrService = inject(ToastrService);
    private permService = inject(PermissionsService);
    private tabService = inject(TabService);
    private _pages = inject(Pages);
    private _settingsService = inject(SettingsDataService);

    tab: TabModel;

    public requestModel: BusinessNavigatorRequestModel;
    public permissions: BusinessNavigatorPermissionModel;
    public hideActionBar = false;

    public processSideMenuKey = "menuItemProccesses";
    static processSideMenuKeyReference = "menuItemProccesses";

    public documentsSideMenuKey = "menuItemDocuments";
    public assessmentDocumentsSideMenuKey = "menuItemAssessmentDocuments";
    public hyperlinksSideMenuKey = "menuItemHyperlinks";
    public historySideMenuKey = "menuItemHistory";

    public useIncidents = false;

    @ViewChild(ProcessEditorComponent)
    private processEditorComponent: ProcessEditorComponent;

    @ViewChild(BusinessDocumentsComponent)
    private businessDocumentsComponent: BusinessDocumentsComponent;

    @ViewChild(BusinessAssessmentDocumentsComponent)
    private businessAssessmentDocumentsComponent: BusinessAssessmentDocumentsComponent;

    @ViewChild(BusinessHyperlinksComponent)
    private businessHyperlinksComponent: BusinessHyperlinksComponent;

    @ViewChild(HistoryOverviewComponent)
    private historyOverviewComponent: HistoryOverviewComponent;

    get isStoredAssessment(): boolean {
        return this.requestModel && this.requestModel.hasAssessment;
    }

    get castedTabConfig(): BusinessNavigatorRequestModel {
        const filtersPassedThrough =
            this.tab.config &&
            ((this.tab.config as BusinessNavigatorRequestModel).organizationID ||
                (this.tab.config as BusinessNavigatorRequestModel).businessID);

        if (filtersPassedThrough) {
            return this.tab.config as BusinessNavigatorRequestModel;
        }

        // This is needed for mailmessages, that link to a specific assessment through the url.
        if (this.tab.config && (this.tab.config.organizationid || this.tab.config.businessid)) {
            return <BusinessNavigatorRequestModel>{
                organizationID: +this.tab.config.organizationid,
                businessID: +this.tab.config.businessid,
            };
        }

        return null;
    }

    public isSavingDocuments = false;
    public isDocumentMenuItem = false;
    get showDocumentButton() {
        return (
            this.isDocumentMenuItem &&
            this.businessDocumentsComponent &&
            this.businessDocumentsComponent.documentsLoaded &&
            this.requestModel.businessID > 0
        );
    }

    public isSavingAssessmentDocuments = false;
    public isAssessmentDocumentMenuItem = false;
    get showAssessmentDocumentButton() {
        return (
            this.isAssessmentDocumentMenuItem &&
            this.businessAssessmentDocumentsComponent &&
            this.businessAssessmentDocumentsComponent.documentsLoaded
        );
    }

    public applicationPermissions: ApplicationPermissionsModel;

    async ngOnInit() {
        this.applicationPermissions = this.permService.permissions;
        this.useIncidents = (
            await firstValueFrom(this._settingsService.getSetting(ApplicationSettings.UseIncidents))
        ).BoolValue;

        if (this.castedTabConfig) {
            // Load existing
            await this.loadAssessmentConfiguration(
                this.castedTabConfig.organizationID,
                this.castedTabConfig.businessID
            );
        } else {
            // Load popup
            await this.startAssessmentConfiguration();
        }
    }

    public onMenuItemClick(menuItem: TabMenuItem) {
        if (!menuItem || !menuItem.menuItemId) {
            return;
        }

        let hideActionBar = false;
        this.isDocumentMenuItem = false;
        this.isAssessmentDocumentMenuItem = false;

        switch (menuItem.menuItemId) {
            case this.processSideMenuKey: {
                this.processEditorComponent.loadDiagram({
                    businessDimension: this.requestModel.businessID,
                    organization: this.requestModel.organizationID,
                });

                break;
            }
            case this.documentsSideMenuKey: {
                this.isDocumentMenuItem = true;
                this.businessDocumentsComponent.loadDocuments();
                break;
            }
            case this.assessmentDocumentsSideMenuKey: {
                this.isAssessmentDocumentMenuItem = true;
                this.businessAssessmentDocumentsComponent.loadDocuments();
                break;
            }
            case this.hyperlinksSideMenuKey: {
                this.businessHyperlinksComponent.loadHyperlinks();
                break;
            }
            case this.historySideMenuKey: {
                hideActionBar = true;
                this.historyOverviewComponent.loadHistory();
                break;
            }
        }

        this.hideActionBar = hideActionBar;
    }

    public async saveDocuments() {
        this.isSavingDocuments = true;
        this.businessDocumentsComponent.saveDocuments().finally(() => {
            this.isSavingDocuments = false;
            this.businessDocumentsComponent.loadDocuments(true);
        });
    }

    public async saveAssessmentDocuments() {
        this.isSavingAssessmentDocuments = true;
        this.businessAssessmentDocumentsComponent.saveDocuments().finally(() => {
            this.isSavingAssessmentDocuments = false;
            this.businessAssessmentDocumentsComponent.loadDocuments(true);
        });
    }

    private async startAssessmentConfiguration() {
        const fieldResult: BusinessNavigatorRequestModel = {
            organizationID: null,
            businessID: null,
            hasAssessment: false,
            businessAssessmentID: null,
        };

        const configPrompt = this.promptService
            .prompt({
                data: {
                    promptUiid: "Business-navigator-prompt",
                    title: "Business Assessment Configuration",
                    message:
                        "Please select the organization and business dimension the assessment should be based upon. Note that without an organization selection the review cannot be initiated.",
                    fieldResult: fieldResult,
                    fields: [
                        {
                            prettyName: "Organization",
                            fieldName: nameof<BusinessNavigatorRequestModel>(
                                (x) => x.organizationID
                            ),
                            fieldType: GenericListFieldType.SingleTree,
                            uiid: "business-org-select",
                            selfInit: true,
                            getDataMethod: () => {
                                return this.dataService.getOrganizations();
                            },
                        },
                        {
                            prettyName: "Business Dimension",
                            fieldName: nameof<BusinessNavigatorRequestModel>((x) => x.businessID),
                            fieldType: GenericListFieldType.SingleTree,
                            uiid: "business-bd-select",
                            selfInit: true,
                            getDataMethod: () => {
                                return this.dataService.getBusinessDimensionsByOrganization(
                                    fieldResult.organizationID
                                );
                            },
                            checkReloadDataByRowChanges: (prev, current) => {
                                const prevModel = prev as BusinessNavigatorRequestModel;
                                const currentModel = current as BusinessNavigatorRequestModel;
                                const hasChanged =
                                    prevModel.organizationID != currentModel.organizationID;
                                if (hasChanged) {
                                    fieldResult.businessID = null;
                                }
                                return hasChanged;
                            },
                        },
                    ],

                    confirmButton: {
                        uiid: "business-popup-load-button",
                        text: "Load",
                        icon: "fas",
                    },
                },
            })
            .getResult();

        const result = await firstValueFrom(configPrompt);
        if (!result) {
            this.tab.close(false);
            return;
        }

        await this.loadAssessmentConfiguration(result.organizationID, result.businessID);
    }

    private async loadAssessmentConfiguration(orgID?: number, bdID?: number) {
        if (!orgID && !bdID) {
            this.toastrService.warning(
                "At least one organization or business needs to be selected.",
                "Load failed"
            );
            await this.startAssessmentConfiguration();
            return;
        }

        this.dataService
            .getRequestValidation({
                organizationID: orgID,
                businessID: bdID,
                hasAssessment: false,
            })
            .then(async (data) => {
                this.requestModel = data;

                await this.loadPermissions(orgID, bdID);
                this.tab.showLoader = false;
            })
            .catch(async (_) => {
                if (this.castedTabConfig) {
                    this.tab.close(false);
                } else {
                    await this.startAssessmentConfiguration();
                }
            });
    }

    async loadPermissions(orgID?: number, bdID?: number) {
        var request = this.dataService.getPermissions(orgID, bdID);
        request.catch(() => {
            this.tab.close(false);
        });

        this.permissions = await request;
    }

    delete() {
        this.dataService.deleteAssessment(this.requestModel).subscribe({
            next: (validations: string[]) => {
                if (validations && validations.length > 0) {
                    this.toastrService.warning(validations.toString());
                } else {
                    this.toastrService.success("Business Assessment deleted");
                    this.tabService.refresh(this._pages.BusinessNavigator);
                    this.tab.close(false);
                }
            },
            error: (error) => {
                this.toastrService.error("", error || error.Message);
            },
        });
    }
}
